import React, { useState } from "react";
import { FaCheckSquare, FaLink, FaFolder } from "react-icons/fa";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

function CheckList() {
  const [hoveredId, setHoveredId] = useState(null);

  const checklistOptions = [
    {
      id: "create-category",
      title: "Manage Categories",
      description:
        "Create and manage categories (e.g., 'Documents', 'Pre-Departure') to organize your checklists.",
      icon: <FaFolder className="text-white text-xl" />,
      color: "from-blue-600 to-blue-700",
      accentColor: "bg-blue-600",
      link: "/checklist/checklist-category",
    },
    {
      id: "create-checklist",
      title: "Create Checklist",
      description:
        "Build new checklists with categorized items for education or migration applications.",
      icon: <FaCheckSquare className="text-white text-xl" />,
      color: "from-emerald-600 to-emerald-700",
      accentColor: "bg-emerald-600",
      link: "/checklist/create",
    },
    {
      id: "attach-checklist",
      title: "Attach Checklist To Providers",
      description:
        "Assign existing checklists to providers' courses or specific visa types.",
      icon: <FaLink className="text-white text-xl" />,
      color: "from-purple-600 to-purple-700",
      accentColor: "bg-purple-600",
      link: "/checklist/attach",
    },
  ];

  return (
    <div className="card">
      {/* Header */}
      <header className="border-b border-border/30 bg-gradient-to-b from-background to-background/50 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto  sm:py-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground">
            Manage Checklist
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base">
            Create checklists, define categories, and assign them to courses or
            visa types.
          </p>
        </div>
      </header>

      {/* Cards */}
      <div className="max-w-6xl mx-auto px-4 py-12 sm:py-12">
        <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {checklistOptions.map((option) => (
            <div
              key={option.id}
              onMouseEnter={() => setHoveredId(option.id)}
              onMouseLeave={() => setHoveredId(null)}
              className="group cursor-pointer h-full"
            >
              <Link to={option.link} className="block h-full no-underline">
                <div
                  className={`relative h-full bg-card border border-border/40 rounded-2xl p-6 sm:p-8
                    transition-all duration-300 ease-out
                    hover:border-primary/50 hover:shadow-lg hover:shadow-primary/10
                    ${
                      hoveredId === option.id
                        ? "scale-[1.02] bg-gradient-to-br from-card to-card/50"
                        : "hover:scale-[1.01]"
                    }`}
                >
                  <div
                    className={`absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-300
                      ${hoveredId === option.id ? "opacity-100" : ""}`}
                    style={{
                      background:
                        hoveredId === option.id
                          ? `linear-gradient(135deg, ${
                              option.color === "from-blue-600 to-blue-700"
                                ? "rgba(37, 99, 235, 0.08)"
                                : option.color ===
                                  "from-emerald-600 to-emerald-700"
                                ? "rgba(5, 150, 105, 0.08)"
                                : "rgba(147, 51, 234, 0.08)"
                            })`
                          : "transparent",
                    }}
                  />

                  <div className="relative z-10 flex items-start gap-4 h-full flex-col">
                    {/* Icon */}
                    <div
                      className={`${
                        option.accentColor
                      } p-4 rounded-xl shadow-md transition-all duration-300
                        ${
                          hoveredId === option.id
                            ? "scale-110 shadow-lg shadow-current/30"
                            : "group-hover:shadow-md"
                        }`}
                    >
                      {option.icon}
                    </div>

                    {/* Text */}
                    <div className="flex-1 space-y-2 w-full">
                      <h3
                        className={`text-lg sm:text-xl font-semibold text-foreground transition-all duration-300
                        ${hoveredId === option.id ? "text-primary" : ""}`}
                      >
                        {option.title}
                      </h3>

                      <p
                        className={`text-sm text-muted-foreground leading-relaxed transition-colors duration-300
                        ${
                          hoveredId === option.id
                            ? "text-muted-foreground/80"
                            : ""
                        }`}
                      >
                        {option.description}
                      </p>
                    </div>

                    {/* Arrow */}
                    <div className="pt-2 w-full">
                      <ArrowRight
                        className={`text-muted-foreground transition-all duration-300
                          ${
                            hoveredId === option.id
                              ? "text-primary translate-x-1"
                              : "group-hover:translate-x-0.5"
                          }`}
                        size={20}
                      />
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default CheckList;
